import React from "react";
import { SingleStageCardProps, StageProgram, getValueColor } from "./cardTypes";
import { STATUS_CONFIG } from "../constants";

const getStatusColor = (status: string | undefined): string | undefined => {
  if (!status) return undefined;
  const cfg = STATUS_CONFIG[status];
  return cfg ? cfg.color : undefined;
};

// Convert API decimal value to percentage (e.g. 0.85 -> 85)
// Handles null, undefined, empty string, and NaN gracefully
const toNum = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return isNaN(n) ? null : n * 100;
};

// Extract current period value for a given stage
const getVal = (p: StageProgram, stageKey: string, valueField: string): number | null =>
  toNum(p.stages[stageKey]?.[valueField]);

// Extract previous period value for a given stage
const getPrev = (p: StageProgram, stageKey: string, prevField: string): number | null =>
  toNum(p.stages[stageKey]?.[prevField]);

const formatValue = (v: number, format: "number" | "percent") =>
  format === "percent" ? `${v.toFixed(2)}%` : v.toLocaleString();

export const SingleStageCard: React.FC<SingleStageCardProps> = ({
  programs, stageConfigs, warningOffset, invert = false,
}) => {
  // Returns green/red color based on whether the trend is positive (invert flips the logic for metrics like downtime)
  const trendColor = (delta: number) => {
    const good = invert ? delta <= 0 : delta >= 0;
    return good ? "#1d8102" : "#d13212";
  };

  // Formats the trend delta with arrow indicator and percentage (e.g. "▲+1.50%" or "▼-0.80%")
  const trendLabel = (delta: number) => {
    if (invert) {
      const good = delta <= 0;
      return `${good ? "▼" : "▲"}${delta >= 0 ? "+" : ""}${delta.toFixed(2)}%`;
    }
    return `${delta >= 0 ? "▲+" : "▼"}${delta.toFixed(2)}%`;
  };

  // Filter sub-programs once to avoid repeated filtering in render
  const subPrograms = programs.filter((p) => p.parent_program != null);

  return (
    <div data-testid="single-stage-card" className="card-program-list metric-card-container">
      <div className="stage-flex-wrap">
        {stageConfigs.map((stage) => {
          // Use parent program's data as the main display value
          const parentProgram = programs.find((p) => p.parent_program == null);
          // Check if parent has a valid value for this stage (not just the stage key existing)
          const parentHasStage = parentProgram?.stages[stage.key]?.[stage.valueField] != null;

          // Fallback: if parent doesn't have this stage, use the first sub-program that has it
          let displayProgram: StageProgram | undefined;
          if (parentHasStage) {
            displayProgram = parentProgram;
          } else {
            displayProgram = programs.find((p) => p.parent_program != null && p.stages[stage.key]?.[stage.valueField] != null);
          }

          const mainVal = displayProgram ? getVal(displayProgram, stage.key, stage.valueField) : null;
          const mainPrev = displayProgram ? getPrev(displayProgram, stage.key, stage.prevField) : null;
          const parentStageStatus = displayProgram?.stages[stage.key]?.status as string | undefined;

          // Trend delta: difference between current and previous period
          const delta = mainVal != null && mainPrev != null ? mainVal - mainPrev : null;

          return (
            <div key={stage.key} className="stage-block" data-testid={`stage-block-${stage.key}`}>
              <div className="stage-block-main">
                <div className="stage-block-label-row">
                  <span className="stage-label" data-testid={`stage-label-${stage.key}`}>{stage.label}</span>
                </div>
                {/* Main value and trend */}
                {mainVal != null ? (
                  <div className="stage-value-row">
                    <span className="stage-value" data-testid={`stage-value-${stage.key}`} style={{ color: getStatusColor(parentStageStatus) || getValueColor(mainVal, stage.target, warningOffset, invert) }}>
                      {mainVal.toFixed(1)}<span className="stage-unit">%</span>
                    </span>
                    <span className="stage-trend" data-testid={`stage-trend-${stage.key}`} style={{ color: delta != null ? trendColor(delta) : "#8d9baa" }}>
                      {delta != null ? trendLabel(delta) : "—"}
                    </span>
                  </div>
                ) : (
                  <div className="stage-value-row">
                    <span className="stage-value" data-testid={`stage-value-${stage.key}`} style={{ color: "#8d9baa" }}>—</span>
                  </div>
                )}

                {/* Detail metrics (e.g. Actual,Plan,Cum) */}
                {stage.details && stage.details.length > 0 && (
                  <div className="stage-detail" data-testid={`stage-detail-${stage.key}`}>
                    {stage.details.map((d, idx) => {
                      const rawVal = displayProgram?.stages[stage.key]?.[d.field];
                      const rawDetail = rawVal != null ? Number(rawVal) : NaN;
                      const detailVal = !isNaN(rawDetail)
                        ? (d.format === "percent" ? rawDetail * 100 : rawDetail)
                        : null;
                      return (
                        <React.Fragment key={d.field}>
                          {idx > 0 && <span className="stage-detail-separator">|</span>}
                          {d.label}{" "}
                          <span className="stage-detail-value" data-testid={`stage-${d.label.toLowerCase()}-${stage.key}`}>
                            {detailVal != null ? formatValue(detailVal, d.format) : "—"}
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sub-program rows */}
              <div className="stage-subrow-container" style={subPrograms.length === 0 ? { borderTop: 'none' } : undefined} data-testid={`stage-sub-list-${stage.key}`}>
                {subPrograms.map((p) => {
                  const val = getVal(p, stage.key, stage.valueField);
                  const prev = getPrev(p, stage.key, stage.prevField);
                  const subDelta = val != null && prev != null ? val - prev : null;
                  return (
                    <div key={`${p.program}-${p.cm}-${p.cm_location}`} className="stage-subrow" data-testid={`stage-subrow-${stage.key}-${p.program}`}>
                      <span title={p.program} className="stage-subrow-name">{p.program}</span>
                      <span className="stage-subrow-values">
                        {val != null ? (
                          <>
                            <span className="stage-subrow-value" data-testid={`stage-subrow-value-${stage.key}-${p.program}`} style={{ color: getStatusColor(p.stages[stage.key]?.status as string | undefined) || getValueColor(val, stage.target, warningOffset, invert) }}>{val.toFixed(2)}%</span>
                            <span className="stage-trend" data-testid={`stage-subrow-trend-${stage.key}-${p.program}`} style={{ color: subDelta != null ? trendColor(subDelta) : "#8d9baa" }}>
                              {subDelta != null ? trendLabel(subDelta) : "—"}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="stage-subrow-value-empty">—</span>
                            <span className="stage-subrow-trend-empty">—</span>
                          </>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
